import {
  Button,
  Popover,
  SegmentedControl,
  Select,
  Stack,
} from "@mantine/core";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import SvgIconRaw from "~/components/SvgIconRaw";

import { POPOVER_COMBOBOX_PROPS } from "./FiltersPopover";
import { getColumnDataKey } from "./filtering";
import type { ImagesSortState } from "./types";

/**
 * Grid-mode sort control. It edits the same sort the table's column headers
 * do, so a column chosen here is the column the table opens sorted by.
 */
const GridSortControl = ({
  columns,
  onSortChange,
  sort,
}: {
  columns: MeqColumnDef<MeqRowData>[];
  onSortChange: (sort: ImagesSortState | undefined) => void;
  sort: ImagesSortState | undefined;
}) => {
  const { t } = useTranslation("camera");
  const [isOpen, setIsOpen] = useState(false);

  const options = useMemo(
    () =>
      columns.flatMap((column) => {
        const key = getColumnDataKey(column);
        return key ? [{ value: key, label: column.header }] : [];
      }),
    [columns],
  );

  const selectedLabel = options.find(
    (option) => option.value === sort?.columnKey,
  )?.label;

  return (
    <Popover
      opened={isOpen}
      onChange={setIsOpen}
      position="bottom-start"
      shadow="md"
      width={280}
    >
      <Popover.Target>
        <Button
          variant="secondary"
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
          leftSection={<SvgIconRaw name="SwitchVertical" size={16} />}
        >
          {selectedLabel
            ? t("imagesModal.sortByColumn", { column: selectedLabel })
            : t("imagesModal.sortBy")}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={12}>
          <Select
            label={t("imagesModal.sortBy")}
            placeholder={t("imagesModal.sortNone")}
            data={options}
            value={sort?.columnKey ?? null}
            onChange={(columnKey) =>
              onSortChange(
                columnKey
                  ? { columnKey, isAscending: sort?.isAscending ?? false }
                  : undefined,
              )
            }
            comboboxProps={POPOVER_COMBOBOX_PROPS}
            searchable
            clearable
          />
          <SegmentedControl
            aria-label={t("imagesModal.sortDirection")}
            disabled={!sort}
            value={sort?.isAscending ? "asc" : "desc"}
            onChange={(direction) =>
              sort &&
              onSortChange({ ...sort, isAscending: direction === "asc" })
            }
            data={[
              { label: t("imagesModal.sortAscending"), value: "asc" },
              { label: t("imagesModal.sortDescending"), value: "desc" },
            ]}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default GridSortControl;
